import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertCircle, MonitorPlay } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Brand } from "@/components/Layout";
import { portalAssetUrl } from "@/lib/assets";

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [demo, setDemo] = useState(null);

  useEffect(() => { api.get("/auth/demo-access").then((response) => setDemo(response.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const openDemo = async () => {
    if (!demo?.enabled) return;
    setEmail(demo.email); setPassword(demo.password); setError(""); setLoading(true);
    try { await login(demo.email, demo.password); navigate("/"); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Visual panel */}
      <div className="relative hidden w-1/2 overflow-hidden lg:block">
        <img src="https://images.unsplash.com/photo-1496247749665-49cf5b1022e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="Industrieel" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#2b3338]/95 via-[#2b3338]/85 to-[#465259]/60" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
          <div className="self-start"><Brand /></div>
          <div>
            <h1 className="font-heading text-4xl font-black leading-tight tracking-tight xl:text-5xl">{t("Olie-reinheid", "Oil cleanliness")}<br />{t("in één oogopslag.", "at a glance.")}</h1>
            <p className="mt-4 max-w-md text-base text-slate-300">{t("Remote monitoring van de conditie van uw hydraulische systemen. Realtime inzicht, historische trends en slimme statusindicatoren.", "Remote monitoring of the condition of your hydraulic systems. Real-time insight, historical trends and smart status indicators.")}</p>
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Total Hydraulics — Remote Monitoring</div>
        </div>
      </div>

      {/* Form */}
      <div className="flex w-full items-center justify-center p-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 inline-flex rounded-lg bg-[#465259] px-4 py-3 lg:hidden">
            <Brand compact />
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">{t("Welkom terug", "Welcome back")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("Log in om uw installaties te monitoren.", "Sign in to monitor your installations.")}</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {error && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive" data-testid="login-error">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> <span>{error}</span>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">{t("E-mailadres", "Email address")}</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("naam@bedrijf.nl", "name@company.com")} required data-testid="login-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">{t("Wachtwoord", "Password")}</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="login-password" />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("Inloggen…", "Signing in…")}</> : t("Inloggen", "Sign in")}
            </Button>
          </form>

          {demo?.enabled && (
            <div className="mt-6 rounded-xl border border-primary/25 bg-primary/5 p-4" data-testid="public-demo-login">
              <div className="flex items-start gap-3"><div className="rounded-md bg-primary/10 p-2 text-primary"><MonitorPlay className="h-4 w-4" /></div><div><div className="text-sm font-bold">{t("Bekijk onze testbank live", "View our live test bench")}</div><p className="mt-1 text-xs leading-5 text-muted-foreground">{t("Bekijk actuele metingen en historische trends. Dit account kan niets bedienen of wijzigen.", "View live measurements and historical trends. This account cannot control or change anything.")}</p></div></div>
              <div className="mt-3 rounded-md border border-border bg-background/80 px-3 py-2 font-mono text-[11px]"><div>{t("E-mail", "Email")}: {demo.email}</div><div>{t("Wachtwoord", "Password")}: {demo.password}</div></div>
              <Button type="button" variant="outline" className="mt-3 w-full gap-2" onClick={openDemo} disabled={loading} data-testid="open-public-demo"><MonitorPlay className="h-4 w-4" />{t("Testbank live bekijken", "View live test bench")}</Button>
            </div>
          )}

          <div className="mt-6 rounded-md border border-border bg-muted/40 p-4 text-xs text-muted-foreground">
            {t("Beveiligde THRM-omgeving. Accounts en apparaattoegang worden door Total Hydraulics beheerd.", "Secured THRM environment. Accounts and device access are managed by Total Hydraulics.")}
          </div>
        </div>
      </div>
      <img src={portalAssetUrl("favicon.png")} alt="" aria-hidden="true" className="pointer-events-none fixed bottom-3 right-3 z-20 h-7 w-7 rounded-lg opacity-55 shadow-sm sm:bottom-4 sm:right-4" />
    </div>
  );
}
